/**
 * useShellDomainState - the shell's own state: the attack controls, the menus,
 * the roster, the prompts and modals, and the refs callbacks late-bind through.
 *
 * The first of the shell's domain hooks. Every domain hook reads the names it
 * needs from the shell scope `s` (see DeathWatchSimulator) and returns the
 * names it declares, which the shell adds to that scope. Hook order is the
 * shell's original order, unchanged. Extracted from DeathWatchSimulator.
 */
import { useState, useRef } from 'react';
import useRoster from './useRoster';
import { useMacroStore } from './useShellMacros';

/**
 * The attack controls, the menus and templates, and the roster. Length: one
 * line per piece of state and its name in the return record (declarative).
 */
function useShellControlState(s) {
  const { initialSession } = s;
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [editingSlot, setEditingSlot] = useState(null);
  const [fireMode, setFireMode] = useState('single');
  const [hatred, setHatred] = useState(false);
  const [targetLocation, setTargetLocation] = useState('auto');
  /**
   * 'auto' means "whatever the map says". It is the DEFAULT, and picking
   * a band from the Range control overrides it - the same shape as
   * cover's pin, and for the same reason: the map knows, until a person
   * says otherwise.
   *
   * The stored value is the CHOICE; `rangeBand` (the map domain) is the answer.
   */
  const [rangeBandChoice, setRangeBand] = useState('auto');
  const [showAtkMenu, setShowAtkMenu] = useState(false);
  const [newAtkName, setNewAtkName] = useState('');
  const [connectedPlayers, setConnectedPlayers] = useState([]);
  // The GM's vox groups dialog (v8.363.6).
  const [showChannels, setShowChannels] = useState(false);
  const [slotClipboard, setSlotClipboard] = useState(null);
  const [dragSlot, setDragSlot] = useState(null);
  const [dragTab, setDragTab] = useState(null);
  const [tgtTemplates, setTgtTemplates] = useState({});
  const [showTgtMenu, setShowTgtMenu] = useState(false);
  const [newTgtName, setNewTgtName] = useState('');
  // Roster: the single source of truth for every combatant. `tabs`,
  // `setTabs`, `activeTab` and `setActiveTab` are LEGACY SHIMS derived from
  // it (engine/roster/legacyAdapter) so the component tree is untouched
  // while the data model changes underneath - see dev-docs/architecture.md.
  const {
    roster,
    setRoster,
    actorId,
    setActorId,
    targetId: _targetId,
    setTargetId: _setTargetId,
    actor,
    target: targetCombatant,
    updateCombatantState,
    updateCombatantSheet,
    editCombatant,
    appendNarrative,
    restoreStates,
    tabs: rosterTabs,
    setTabs,
    activeTab,
    setActiveTab,
    pointAtIfUnset,
  } = useRoster({ initialSession });
  return {
    selectedSlot,
    setSelectedSlot,
    editingSlot,
    setEditingSlot,
    fireMode,
    setFireMode,
    hatred,
    setHatred,
    targetLocation,
    setTargetLocation,
    rangeBandChoice,
    setRangeBand,
    showAtkMenu,
    setShowAtkMenu,
    newAtkName,
    setNewAtkName,
    connectedPlayers,
    setConnectedPlayers,
    showChannels,
    setShowChannels,
    slotClipboard,
    setSlotClipboard,
    dragSlot,
    setDragSlot,
    dragTab,
    setDragTab,
    tgtTemplates,
    setTgtTemplates,
    showTgtMenu,
    setShowTgtMenu,
    newTgtName,
    setNewTgtName,
    roster,
    setRoster,
    actorId,
    setActorId,
    actor,
    targetCombatant,
    updateCombatantState,
    updateCombatantSheet,
    editCombatant,
    appendNarrative,
    restoreStates,
    rosterTabs,
    setTabs,
    activeTab,
    setActiveTab,
    pointAtIfUnset,
  };
}

/**
 * Prompts, modals, folders, the macro store and the late-binding refs.
 * Length: one line per piece of state and its name in the return record.
 */
function useShellPanelState() {
  const [renamingTab, setRenamingTab] = useState(null);
  const [tabCtx, setTabCtx] = useState(null);
  const [pendingAttack, setPendingAttack] = useState(null);
  const [showHotkeys, setShowHotkeys] = useState(false);
  // Per-profile macros, PERSISTED (v8.327.0) - see `hooks/useShellMacros`.
  const [attackMacros, setAttackMacros] = useMacroStore();
  const [showPermPanel, setShowPermPanel] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showCharacterLibrary, setShowCharacterLibrary] = useState(false);
  const [showCharacterEditor, setShowCharacterEditor] = useState(false);
  const [editingCharacter, setEditingCharacter] = useState(null); // Character | null (null = new)
  // The character folder, once per visit - see CharacterLibrary.folderFiles.
  const [folderFiles, setFolderFiles] = useState(null);
  // And the MAPS folder, for the same reason: the toolbar pane unmounts
  // when Build closes, and holding the picked `File`s here means you do
  // not choose the folder again every time you open it.
  const [mapFolderFiles, setMapFolderFiles] = useState(null);
  const [rerollDamage, setRerollDamage] = useState(false);

  const narrativeRef = useRef(null);
  /** See the `onPings` note on `useBattlefieldSync` (the sync domain). */
  const mergePingsRef = useRef(() => {});
  /** Same reason: a ping is logged from a callback declared before the writer. */
  const addNarrativeRef = useRef(null);
  const fileInputRef = useRef(null);
  return {
    renamingTab,
    setRenamingTab,
    tabCtx,
    setTabCtx,
    pendingAttack,
    setPendingAttack,
    showHotkeys,
    setShowHotkeys,
    attackMacros,
    setAttackMacros,
    showPermPanel,
    setShowPermPanel,
    showSettings,
    setShowSettings,
    showCharacterLibrary,
    setShowCharacterLibrary,
    showCharacterEditor,
    setShowCharacterEditor,
    editingCharacter,
    setEditingCharacter,
    folderFiles,
    setFolderFiles,
    mapFolderFiles,
    setMapFolderFiles,
    rerollDamage,
    setRerollDamage,
    narrativeRef,
    mergePingsRef,
    addNarrativeRef,
    fileInputRef,
  };
}

/**
 * @param {object} s - the shell scope; reads `initialSession` (the roster's
 *   opening session - see components/shell/shellSession).
 * @returns {object} every piece of the shell's own state and its setter, the
 *   roster's record, and the refs.
 */
export default function useShellDomainState(s) {
  const controls = useShellControlState(s);
  const panels = useShellPanelState();
  return { ...controls, ...panels };
}
